import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Stack,
  Button,
  LinearProgress,
  Avatar,
  Divider,
  useTheme,
  alpha,
  IconButton,
  TextField,
  InputAdornment,
  Fade,
  Collapse,
  Alert,
  AlertTitle,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  keyframes
} from '@mui/material';
import {
  EventNote,
  TrendingUp,
  Psychology,
  EmojiEvents,
  Stars,
  FitnessCenter,
  Restaurant,
  Assignment,
  Edit,
  Delete,
  Add,
  Search,
  ExpandMore,
  ExpandLess,
  Schedule,
  Flag,
  MoreVert,
  Print,
  Download
} from '@mui/icons-material';
import { formatDateSafe, toDate } from '../../shared/utils/dateHelpers';
import { formatClientName } from '../../shared/utils/nameHelpers';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface CheckIn {
  id: string;
  check_in_type?: '2_week' | '10_week';
  response: string;
  submitted_at: any;
  submitted_by: string;
}

interface NPSData {
  rating: number;
  comment?: string;
  received?: boolean;
  received_date?: any;
}

interface ClientCheckInsViewProps {
  client: any;
  checkIns: {
    twoWeek: CheckIn[];
    tenWeek: CheckIn[];
  };
  npsData: NPSData | null;
  isLoading: boolean;
  userRole: string | null;
  onAddCheckIn: (type: '2_week' | '10_week') => void;
  onEditCheckIn: (checkIn: CheckIn) => void;
  onDeleteCheckIn: (checkInId: string) => void;
}

const ClientCheckInsView: React.FC<ClientCheckInsViewProps> = ({
  client,
  checkIns,
  npsData,
  isLoading,
  userRole,
  onAddCheckIn,
  onEditCheckIn,
  onDeleteCheckIn
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCards, setExpandedCards] = useState<{ [key: string]: boolean }>({});
  const [overflowCards, setOverflowCards] = useState<{ [key: string]: boolean }>({});
  const textRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCheckIn, setSelectedCheckIn] = useState<CheckIn | null>(null);

  // Calculate check-in status and due dates
  const checkInStatus = useMemo(() => {
    const programStartDate = client?.programme_start_date ? toDate(client.programme_start_date) : null;
    const now = new Date();
    
    if (!programStartDate) {
      return {
        twoWeek: { isDue: false, dueDate: null, isOverdue: false, daysUntilDue: null },
        tenWeek: { isDue: false, dueDate: null, isOverdue: false, daysUntilDue: null }
      };
    }

    const twoWeekDue = new Date(programStartDate);
    twoWeekDue.setDate(twoWeekDue.getDate() + 14);
    
    const tenWeekDue = new Date(programStartDate);
    tenWeekDue.setDate(tenWeekDue.getDate() + 70);

    const daysSinceTwoWeek = Math.floor((now.getTime() - twoWeekDue.getTime()) / (1000 * 60 * 60 * 24));
    const daysSinceTenWeek = Math.floor((now.getTime() - tenWeekDue.getTime()) / (1000 * 60 * 60 * 24));

    return {
      twoWeek: {
        isDue: now >= twoWeekDue && checkIns.twoWeek.length === 0,
        dueDate: twoWeekDue,
        isOverdue: daysSinceTwoWeek > 7 && checkIns.twoWeek.length === 0,
        daysUntilDue: Math.max(0, -daysSinceTwoWeek)
      },
      tenWeek: {
        isDue: now >= tenWeekDue && checkIns.tenWeek.length === 0,
        dueDate: tenWeekDue,
        isOverdue: daysSinceTenWeek > 7 && checkIns.tenWeek.length === 0,
        daysUntilDue: Math.max(0, -daysSinceTenWeek)
      }
    };
  }, [client, checkIns]);

  // Calculate progress metrics
  const progressMetrics = useMemo(() => {
    const totalExpectedCheckIns = 2; // 2-week and 10-week
    const completedCheckIns = (checkIns.twoWeek.length > 0 ? 1 : 0) + (checkIns.tenWeek.length > 0 ? 1 : 0);
    const completionRate = (completedCheckIns / totalExpectedCheckIns) * 100;

    return {
      totalExpected: totalExpectedCheckIns,
      completed: completedCheckIns,
      completionRate,
      npsCompleted: npsData !== null && npsData.received === true && npsData.rating > 0
    };
  }, [checkIns, npsData]);


  const checkOverflow = useCallback(() => {
    const newOverflow: { [key: string]: boolean } = {};
    Object.entries(textRefs.current).forEach(([id, el]) => {
      if (el) {
        newOverflow[id] = el.scrollHeight > el.clientHeight;
      }
    });
    setOverflowCards(prev => {
      const changed = Object.keys(newOverflow).some(k => prev[k] !== newOverflow[k]);
      return changed ? { ...prev, ...newOverflow } : prev;
    });
  }, []);

  const toggleCardExpansion = (cardId: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
    // Re-check overflow after collapse animation settles
    setTimeout(checkOverflow, 50);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, checkIn: CheckIn) => {
    setAnchorEl(event.currentTarget);
    setSelectedCheckIn(checkIn);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCheckIn(null);
  };

  const filteredCheckIns = useMemo(() => {
    const twoWeekWithType = checkIns.twoWeek.map(c => ({ ...c, check_in_type: '2_week' as const }));
    const tenWeekWithType = checkIns.tenWeek.map(c => ({ ...c, check_in_type: '10_week' as const }));
    const allCheckIns = [...twoWeekWithType, ...tenWeekWithType];
    if (!searchQuery) return allCheckIns;
    
    return allCheckIns.filter(checkIn =>
      checkIn.response.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [checkIns, searchQuery]);

  useEffect(() => {
    checkOverflow();
  }, [filteredCheckIns, checkOverflow]);

  const renderCheckInCard = (checkIn: CheckIn, index: number, defaultType?: '2_week' | '10_week') => {
    const isExpanded = expandedCards[checkIn.id] || false;
    const type = checkIn.check_in_type || defaultType || '2_week';
    const checkInType = type === '2_week' ? '2-Week' : '10-Week';
    
    return (
      <Fade in timeout={300 * (index + 1)} key={checkIn.id}>
        <Card 
          elevation={3}
          sx={{ 
            mb: 3,
            borderRadius: 3,
            overflow: 'visible',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
            borderLeft: `4px solid ${type === '2_week' ? theme.palette.info.main : theme.palette.secondary.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: theme.shadows[6],
              transform: 'translateY(-2px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: type === '2_week' ? 'info.main' : 'secondary.main',
                    width: 48,
                    height: 48
                  }}
                >
                  <EventNote />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {checkInType} Check-in
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateSafe(checkIn.submitted_at, 'PPP')} • Submitted by {checkIn.submitted_by}
                  </Typography>
                </Box>
              </Box>
              
              {(userRole === 'MANAGER' || userRole === 'ADMIN') && (
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, checkIn)}
                >
                  <MoreVert />
                </IconButton>
              )}
            </Box>

            {/* Content Preview */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body1"
                ref={(el: HTMLElement | null) => { textRefs.current[checkIn.id] = el; }}
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: isExpanded ? 'unset' : 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {checkIn.response}
              </Typography>
            </Box>

            {/* Expand/Collapse Button */}
            {(overflowCards[checkIn.id] || isExpanded) && (
              <Button
                size="small"
                onClick={() => toggleCardExpansion(checkIn.id)}
                endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                sx={{ mt: 1 }}
              >
                {isExpanded ? 'Show Less' : 'Read More'}
              </Button>
            )}

            {/* Tags/Insights */}
            <Collapse in={isExpanded}>
              <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="subtitle2" gutterBottom>
                  Key Insights
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {extractInsights(checkIn.response).map((insight, idx) => (
                    <Chip
                      key={idx}
                      label={insight.label}
                      size="small"
                      icon={insight.icon}
                      color={insight.color as any}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  function extractInsights(response: string): Array<{ label: string; icon: React.ReactElement; color: string }> {
    const insights = [];
    const lowerResponse = response.toLowerCase();

    if (lowerResponse.includes('progress') || lowerResponse.includes('improvement')) {
      insights.push({ label: 'Progress Noted', icon: <TrendingUp />, color: 'success' });
    }
    if (lowerResponse.includes('challenge') || lowerResponse.includes('difficult')) {
      insights.push({ label: 'Challenges', icon: <Psychology />, color: 'warning' });
    }
    if (lowerResponse.includes('goal') || lowerResponse.includes('target')) {
      insights.push({ label: 'Goal Focused', icon: <EmojiEvents />, color: 'primary' });
    }
    if (lowerResponse.includes('nutrition') || lowerResponse.includes('diet')) {
      insights.push({ label: 'Nutrition', icon: <Restaurant />, color: 'secondary' });
    }
    if (lowerResponse.includes('exercise') || lowerResponse.includes('workout')) {
      insights.push({ label: 'Training', icon: <FitnessCenter />, color: 'info' });
    }

    return insights;
  }

  return (
    <Box sx={{ animation: `${fadeIn} 0.6s ease-out` }}>
      {/* Header Section */}
      <Paper 
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          borderRadius: 3
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Assignment sx={{ mr: 2, fontSize: 36 }} />
              Client Check-ins & Feedback
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track progress milestones and gather valuable feedback throughout {formatClientName(client?.client_name).split(' ')[0]}'s fitness journey
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Box>
                  <Typography variant="h3" color="primary">{progressMetrics.completed}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">Completed</Typography>
                </Box>
                <Box>
                  <Typography variant="h3" color="text.secondary">{progressMetrics.totalExpected}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">Expected</Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <EventNote />
              </Avatar>
              <Typography variant="h4">{progressMetrics.completed}</Typography>
              <Typography variant="body2" color="text.secondary">Check-ins Completed</Typography>
              <Chip 
                label={`${progressMetrics.completionRate}% Complete`} 
                color={progressMetrics.completionRate === 100 ? "success" : "warning"}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <Schedule />
              </Avatar>
              <Typography variant="h4">{checkIns.twoWeek.length}</Typography>
              <Typography variant="body2" color="text.secondary">2-Week Check-ins</Typography>
              {checkInStatus.twoWeek.isDue && (
                <Chip 
                  label={checkInStatus.twoWeek.isOverdue ? "Overdue" : "Due Now"} 
                  color={checkInStatus.twoWeek.isOverdue ? "error" : "warning"}
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'secondary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <Flag />
              </Avatar>
              <Typography variant="h4">{checkIns.tenWeek.length}</Typography>
              <Typography variant="body2" color="text.secondary">10-Week Check-ins</Typography>
              {checkInStatus.tenWeek.isDue && (
                <Chip 
                  label={checkInStatus.tenWeek.isOverdue ? "Overdue" : "Due Now"} 
                  color={checkInStatus.tenWeek.isOverdue ? "error" : "warning"}
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: npsData && npsData.received && npsData.rating > 0 ? 'success.main' : 'grey.500', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <Stars />
              </Avatar>
              <Typography variant="h4">{npsData && npsData.received && npsData.rating > 0 ? `${npsData.rating}/5` : 'N/A'}</Typography>
              <Typography variant="body2" color="text.secondary">NPS Score</Typography>
              <Chip 
                label={npsData && npsData.received && npsData.rating > 0 ? "Completed" : "Pending"} 
                color={npsData && npsData.received && npsData.rating > 0 ? "success" : "default"}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper elevation={2} sx={{ borderRadius: 3 }}>
        <Box sx={{ p: 3 }}>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <Assignment sx={{ mr: 1 }} />
              All Check-ins
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage all check-in responses throughout the client's journey
            </Typography>
          </Box>

          {/* Check-ins View */}
          <Box>
            {/* Search and Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search check-ins..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flexGrow: 1, maxWidth: 400 }}
                  />
                  
                  {(userRole === 'MANAGER' || userRole === 'ADMIN') && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => onAddCheckIn('2_week')}
                        disabled={checkInStatus.twoWeek.daysUntilDue !== null && checkInStatus.twoWeek.daysUntilDue > 0}
                      >
                        Add 2-Week
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<Add />}
                        onClick={() => onAddCheckIn('10_week')}
                        disabled={checkInStatus.tenWeek.daysUntilDue !== null && checkInStatus.tenWeek.daysUntilDue > 0}
                      >
                        Add 10-Week
                      </Button>
                    </Stack>
                  )}
                </Box>

                {/* Check-ins List */}
                {filteredCheckIns.length > 0 ? (
                  <Box>
                    {filteredCheckIns
                      .sort((a, b) => {
                        const dateA = toDate(a.submitted_at);
                        const dateB = toDate(b.submitted_at);
                        return (dateB?.getTime() || 0) - (dateA?.getTime() || 0);
                      })
                      .map((checkIn, index) => renderCheckInCard(checkIn, index))}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <AlertTitle>No check-ins found</AlertTitle>
                    {searchQuery ? 'Try adjusting your search terms.' : 'No check-ins have been recorded yet.'}
                  </Alert>
                )}
              </Box>
        </Box>
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedCheckIn) {
            onEditCheckIn(selectedCheckIn);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Check-in</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedCheckIn) {
            onDeleteCheckIn(selectedCheckIn.id);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Check-in</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <Print fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ClientCheckInsView;